import { useEffect, useState } from "react";
import { Loader2, GitCompare } from "lucide-react";
import { listPrevisionnels, getPrevisionnel } from "@/lib/previsionnels";
import { computeScenarioKpis } from "@/lib/scenarioKpis";

const eur = (v) => Math.round(v || 0).toLocaleString("fr-FR") + " €";
const DSCR_TONE = { sain: "text-emerald-700", tendu: "text-amber-700", defaut: "text-rose-700", na: "text-rose-700", none: "text-slate-400" };
const DSCR_EVAL = { sain: "Sain", tendu: "Tendu", defaut: "Risque", na: "EBE Négatif", none: "—" };

export default function ComparateurScenarios({ dossierId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await listPrevisionnels(dossierId);
                const full = await Promise.all((list || []).map((p) => getPrevisionnel(p.id).catch(() => null)));
                const computed = full.filter(Boolean).map((p) => ({ id: p.id, titre: p.titre || "Sans titre", kpis: computeScenarioKpis(p) }));
                if (!cancelled) setRows(computed);
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [dossierId]);

    if (loading) return <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Calcul des scénarios…</div>;

    return (
        <div data-testid="comparateur-scenarios" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><GitCompare className="h-4 w-4" strokeWidth={1.75} /></span>
                <div><h3 className="font-display text-lg text-foreground">Comparateur de scénarios</h3><p className="text-xs text-muted-foreground">Impact instantané de chaque hypothèse — Résultat d'exploitation, trésorerie cumulée fin de période, DSCR.</p></div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-max text-sm">
                    <thead><tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <th className="py-2 pr-4">Scénario</th>
                        <th className="py-2 px-4 text-right">Résultat d'exploitation</th>
                        <th className="py-2 px-4 text-right">Trésorerie cumulée (fin)</th>
                        <th className="py-2 px-4 text-right">DSCR ({rows[0]?.kpis.mois ?? "—"} mois)</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`comparateur-row-${r.id}`} className="border-b border-slate-100 last:border-0">
                                <td className="py-2.5 pr-4 font-medium text-slate-800">{r.titre}</td>
                                <td data-testid={`comparateur-re-${r.id}`} className={`py-2.5 px-4 text-right tabular-nums ${r.kpis.re >= 0 ? "text-slate-800" : "text-rose-700"}`}>{eur(r.kpis.re)}</td>
                                <td data-testid={`comparateur-treso-${r.id}`} className={`py-2.5 px-4 text-right tabular-nums ${r.kpis.tresoFin >= 0 ? "text-slate-800" : "text-rose-700 font-semibold"}`}>{eur(r.kpis.tresoFin)}</td>
                                <td data-testid={`comparateur-dscr-${r.id}`} className={`py-2.5 px-4 text-right tabular-nums font-semibold ${DSCR_TONE[r.kpis.statut]}`}>
                                    {r.kpis.dscr != null ? r.kpis.dscr.toFixed(2) : DSCR_EVAL[r.kpis.statut]}
                                    {r.kpis.dscr != null && <span className="ml-1 text-[10px] font-normal opacity-70">({DSCR_EVAL[r.kpis.statut]})</span>}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={4} className="py-4 text-sm text-slate-400">Aucun scénario à comparer.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
